import { VAT_RATE_FACTOR } from './constants';

export function nrfFormatCurrency(
  value: number,
  addVat: boolean = false,
  decimalPlaces: number = 2,
  locale: string = 'de-DE',
): string {
  // Add VAT?
  if (addVat) {
    value = value * VAT_RATE_FACTOR;
  }

  // Format
  const formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(value);

  // Intl.NumberFormat doesn't expose currency spacing rules.
  // For de-DE + EUR, browsers usually emit NBSP (U+00A0) before "€".
  // Replace it with a narrow no-break space (U+202F) for a "thin space" look.
  return formatted.replace(/\u00A0(?=€)/g, '\u202F');
}

// Same as nrfFormatCurrency, but whole euro amounts drop the decimals:
// 4.00 -> "4 €" (de) / "€4" (en), 3.36 -> "3,36 €" / "€3.36". For compact
// labels such as price badges and the marketing price list.
export function nrfFormatCurrencyShort(
  value: number,
  addVat: boolean = false,
  locale: string = 'de-DE',
): string {
  const gross = addVat ? value * VAT_RATE_FACTOR : value;
  const isWholeEuro = Math.round(gross * 100) % 100 === 0;

  return nrfFormatCurrency(gross, false, isWholeEuro ? 0 : 2, locale);
}

// A percent value as in the database, 22.222222 -> "22,22 %". Same locale and
// spacing as nrfFormatCurrency.
export function nrfFormatPercent(
  value: number,
  decimalPlaces: number = 2,
): string {
  const formatted = new Intl.NumberFormat('de-DE', {
    style: 'percent',
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(value / 100);

  return formatted.replace(/ (?=%)/g, ' ');
}

// Converts a localized numeric string to a float, supporting both German and English formats
export function nrfParseLocalizedNumber(input: string): number {
  const trimmed = input.trim();

  // Match last occurrence of either comma or dot
  const lastComma = trimmed.lastIndexOf(',');
  const lastDot = trimmed.lastIndexOf('.');

  let decimalSeparator = '';
  if (lastComma > lastDot) {
    decimalSeparator = ',';
  } else if (lastDot > lastComma) {
    decimalSeparator = '.';
  }

  // Remove all commas and dots
  let cleaned = trimmed.replace(/[.,]/g, '');

  // Re-insert decimal separator as dot
  if (decimalSeparator) {
    const decimalIndex = Math.max(lastComma, lastDot);
    cleaned =
      trimmed.slice(0, decimalIndex).replace(/[.,]/g, '') +
      '.' +
      trimmed.slice(decimalIndex + 1).replace(/[.,]/g, '');
  }

  const result = parseFloat(cleaned);
  if (isNaN(result)) {
    throw new Error(`Invalid number format: "${input}"`);
  }

  return result;
}
